import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { getUserProjects } from '../../../features/sessions/projectsManagement/sessionManageProjects';
import { ProjectPlayer } from '../../audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import { format } from 'date-fns';
import { SelectProjectPost } from './SelectProjectPost';
import React from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface ProjectSelectModalProps {
    onOpenModal: Dispatch<SetStateAction<boolean>>;
    setProjectSelectedId: Dispatch<SetStateAction<any>>;
}

export function SelectProjectModal({ onOpenModal, setProjectSelectedId }: ProjectSelectModalProps) {
    const currentUser          = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders          = useSelector((state: RootState) => state.session.authHeaders);
    const loadingProjectsModal = useSelector((state: RootState) => state.sessionManageProjects.loading);
    const allUserProjects      = useSelector((state: RootState) =>
        state.sessionManageProjects.allProjects.filter(p => (p.project as any).userId === currentUser?.id)
    );
    const projectSelectedId = useSelector((state: RootState) => state.sessionProjectPosts.projectSelectedId);
    const dispatch          = useDispatch<AppDispatch>();
    const [projectsPage]    = useState<number>(1);
    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);

    useEffect(() => {
        if (authHeaders && currentUser?.nickname) {
            dispatch(getUserProjects({ authHeaders, urlParams: { userNickname: currentUser.nickname, page: projectsPage } }));
        }
    }, [authHeaders, currentUser]);

    function handleFormatDate(date: any) {
        return format(new Date(date), 'dd MMM yyyy');
    }

    return (
        <Dialog
            open
            maxWidth='sm'
            fullWidth
            onClose={() => onOpenModal(false)}
            PaperProps={{ sx: { bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>Select Project</Typography>
                <IconButton onClick={() => onOpenModal(false)} size='small' sx={{ color: TEXT_SECONDARY }}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <DialogContent sx={{ p: 0 }}>
                {loadingProjectsModal ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: BRAND_RED }} size={32} />
                    </Box>
                ) : allUserProjects.length === 0 ? (
                    <Stack alignItems='center' justifyContent='center' py={6}>
                        <MusicNoteIcon sx={{ fontSize: 48, color: TEXT_SECONDARY, mb: 1 }} />
                        <Typography variant='body2' color={TEXT_SECONDARY}>No projects yet</Typography>
                    </Stack>
                ) : (
                    <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        {allUserProjects.map((item, index) => (
                            <Box
                                key={index}
                                sx={{ borderBottom: BORDER_SUBTLE, px: 2, pt: 1.5, pb: 1, '&:last-child': { borderBottom: 'none' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    {/* Select checkbox */}
                                    <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                                        <SelectProjectPost projectId={item.project.id} />
                                    </Box>

                                    {/* Project info */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant='body2' fontWeight={600} color={TEXT_PRIMARY} noWrap sx={{ flex: 1 }}>
                                                {item.project.title}
                                            </Typography>
                                            <Chip
                                                label={item.project.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                                                size='small'
                                                sx={{
                                                    bgcolor: item.project.privacity === 0 ? 'rgba(35,148,0,0.18)' : 'rgba(192,57,43,0.18)',
                                                    color: item.project.privacity === 0 ? '#4caf50' : '#e57373',
                                                    fontWeight: 600, fontSize: '0.6rem', flexShrink: 0,
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1.5, mb: item.masterFiles?.length ? 0.75 : 0 }}>
                                            <Typography variant='caption' color={TEXT_SECONDARY}>
                                                Useful: <b style={{ color: TEXT_PRIMARY }}>{item.project.nUsefulContents}</b>
                                            </Typography>
                                            <Typography variant='caption' color={TEXT_SECONDARY}>
                                                Other: <b style={{ color: TEXT_PRIMARY }}>{item.project.nUselessContents}</b>
                                            </Typography>
                                        </Box>

                                        {item.masterFiles?.[0] && (
                                            <Box>
                                                <Typography variant='caption' color='rgba(244,244,246,0.35)' display='block' mb={0.5}>
                                                    {handleFormatDate(item.masterFiles[0].createdAt)}
                                                </Typography>
                                                <MemorizedProjectPlayer fileUrl={`${item.masterFiles[0].signedUrl}`} />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant='text'
                    onClick={() => onOpenModal(false)}
                    sx={{ color: TEXT_SECONDARY, textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    startIcon={<AttachFileIcon />}
                    onClick={() => { setProjectSelectedId(projectSelectedId); onOpenModal(false); }}
                    sx={{ bgcolor: BRAND_RED, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#a93226' } }}
                >
                    Attach Project
                </Button>
            </DialogActions>
        </Dialog>
    );
}
